import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import Navbar from "@/components/Navbar";

export default function Signup() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "", password: "", name: "", age: 25,
    gender: "female", interested_in: "male", bio: "", city: "",
    lat: null, lng: null,
  });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Use JPEG, PNG, or WEBP");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(f);
  };

  const getLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setForm(f => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude }));
        toast.success("Location captured");
      },
      () => toast.error("Permission denied — enter city manually")
    );
  };

  const submit = async () => {
    if (!photo) { toast.error("Upload a photo"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { ...form, photo_base64: photo });
      auth.setSession(data.token, data.user);
      toast.success(`You scored ${data.ai_rating ?? "?"}/10`);
      nav("/discover");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="font-mono text-xs uppercase tracking-widest text-[#555] mb-3">
          [ SIGNUP / STEP {step} OF 3 ]
        </div>
        <h1 className="font-display text-5xl tracking-tighter mb-8">Get<br />your<br />rating.</h1>

        {step === 1 && (
          <div className="space-y-5">
            <Field label="Email"><input data-testid="signup-email" type="email" value={form.email} onChange={e => u("email", e.target.value)} className="inp" /></Field>
            <Field label="Password (8+ chars)"><input data-testid="signup-password" type="password" value={form.password} onChange={e => u("password", e.target.value)} className="inp" /></Field>
            <Field label="Full name"><input data-testid="signup-name" value={form.name} onChange={e => u("name", e.target.value)} className="inp" /></Field>
            <Field label="Age">
              <input data-testid="signup-age" type="number" min="18" max="99" value={form.age} onChange={e => u("age", parseInt(e.target.value) || 18)} className="inp" />
            </Field>
            <button data-testid="signup-step1-next" onClick={() => {
              if (!form.email || form.password.length < 8 || !form.name) { toast.error("Fill required fields"); return; }
              setStep(2);
            }} className="w-full py-4 bg-black text-white font-mono">CONTINUE →</button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <Field label="I am">
              <Select testid="signup-gender" value={form.gender} onChange={v => u("gender", v)} options={["female", "male", "other"]} />
            </Field>
            <Field label="Interested in">
              <Select testid="signup-interested" value={form.interested_in} onChange={v => u("interested_in", v)} options={["male", "female", "both"]} />
            </Field>
            <Field label="City"><input data-testid="signup-city" value={form.city} onChange={e => u("city", e.target.value)} placeholder="Berlin" className="inp" /></Field>
            <Field label="Location">
              <button data-testid="signup-geo" type="button" onClick={getLocation} className="px-4 py-3 border border-black font-mono text-xs hover:bg-black hover:text-white">
                {form.lat ? `✓ ${form.lat.toFixed(2)}, ${form.lng.toFixed(2)}` : "USE BROWSER LOCATION"}
              </button>
            </Field>
            <Field label="Short bio (optional)">
              <textarea data-testid="signup-bio" rows="3" value={form.bio} onChange={e => u("bio", e.target.value)} className="inp resize-none" />
            </Field>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="px-6 py-3 border border-black font-mono">← BACK</button>
              <button data-testid="signup-step2-next" onClick={() => setStep(3)} className="flex-1 py-3 bg-black text-white font-mono">CONTINUE →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div className="brutal p-6 bg-white">
              <div className="font-mono text-xs uppercase mb-3">Photo upload</div>
              {photo ? (
                <div className="space-y-3">
                  <img src={photo} alt="preview" className="w-full aspect-square object-cover brutal" />
                  <button onClick={() => { setPhoto(null); fileRef.current.value = ""; }} className="font-mono text-xs underline">CHANGE PHOTO</button>
                </div>
              ) : (
                <button data-testid="signup-photo-pick" onClick={() => fileRef.current.click()} className="w-full aspect-square border-2 border-dashed border-black flex items-center justify-center font-mono text-sm hover:bg-black hover:text-white">
                  TAP TO UPLOAD
                </button>
              )}
              <input ref={fileRef} data-testid="signup-photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" />
              <p className="font-mono text-[10px] text-[#555] mt-3 leading-snug">
                YOUR PHOTO IS SCORED 1-10 BY GPT-4O VISION ON SIGNUP.
                THE COMMUNITY MAY ALSO VOTE. BE BOLD.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="px-6 py-3 border border-black font-mono">← BACK</button>
              <button data-testid="signup-submit" disabled={loading} onClick={submit} className="flex-1 py-3 bg-[#E53935] hover:bg-[#C62828] text-white font-mono disabled:opacity-60">
                {loading ? "SCORING..." : "GET MY SCORE →"}
              </button>
            </div>
          </div>
        )}

        <p className="mt-8 text-sm text-[#555]">
          Have an account? <Link to="/login" data-testid="signup-to-login" className="underline">Log in</Link>
        </p>
      </div>
      <style>{`.inp { width:100%; padding: 12px; background:white; border:1px solid #111; outline:none; }
        .inp:focus { box-shadow: 0 0 0 2px #111; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-xs uppercase mb-2">{label}</div>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map(o => (
        <button
          key={o}
          type="button"
          data-testid={`${testid}-${o}`}
          onClick={() => onChange(o)}
          className={`px-4 py-2 border border-black font-mono text-xs uppercase ${value === o ? "bg-black text-white" : "hover:bg-black hover:text-white"}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
